import IconWorkflowMerge from "@shared/components/icons/WorkflowMerge"
import { useDocumentsTogether } from "@shared/components/providers/SupergrainProvider"
import { cx } from "@shared/components/untitledui/util/cx"
import { calculateCourseDateCombined } from "@shared/lib/course-date-combined"
import type { ParentCardStack, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import UnitsInHeader from "react-app/components/planner/card-stacks/lesson/UnitsInHeader"
import FollowedCourseMenu from "./FollowedCourseMenu"

interface Props {
  course: Course
  followedCourse: Course
  followedPlanbook: Planbook
  followedRotationCalendar: RotationCalendar | null
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  /** The host class isn't meeting this day. */
  isClassOff: boolean
  isCollapsed: boolean
  onCopyFollowedLesson?: (followedLesson: UnifiedCardStack, followedDateString: string) => void
  /** The lesson the host lesson was copied from, which may be one of the followed class's. */
  parentCardStack: ParentCardStack | null
  editCourseTitleAndOrder: (courseId: string) => void
  className?: string
}

export default tracked(function FollowedCourseBar({
  course,
  followedCourse,
  followedPlanbook,
  followedRotationCalendar,
  planbook,
  rotationCalendar,
  dateString,
  isClassOff,
  isCollapsed,
  onCopyFollowedLesson,
  parentCardStack,
  editCourseTitleAndOrder,
  className,
}: Props) {
  return (
    <div
      className={cx(barClassName, className)}
      data-color={course.attributes.color}
      data-test="followed course bar"
    >
      <div className="flex items-center gap-1">
        <IconWorkflowMerge className="size-4 shrink-0" />
        <FollowedCourseMenu
          followingCourseId={course.id}
          followedCourse={followedCourse}
          planbook={followedPlanbook}
          rotationCalendar={followedRotationCalendar}
          dateString={dateString}
          parentCardStack={parentCardStack}
          isCompact={isCollapsed}
          onCopyFollowedLesson={onCopyFollowedLesson}
        />
      </div>
      {/* The followed class's units, whatever lesson was copied, like the host lesson's own units:
      only on a day the host class meets, and not while the planner shows lessons collapsed. */}
      {!isClassOff && !isCollapsed && (
        <FollowedCourseUnits
          followedCourse={followedCourse}
          followedPlanbook={followedPlanbook}
          followedRotationCalendar={followedRotationCalendar}
          lessonModels={{ course, planbook, rotationCalendar }}
          dateString={dateString}
          editCourseTitleAndOrder={editCourseTitleAndOrder}
        />
      )}
    </div>
  )
})

/** Still following a class the teacher can no longer open, so there's nothing to show of it. */
export const FollowedCourseNoAccessBar = tracked(function FollowedCourseNoAccessBar({
  course,
  followedCourse,
  className,
}: {
  course: Course
  followedCourse: Course
  className?: string
}) {
  return (
    <div
      className={cx(barClassName, className)}
      data-color={course.attributes.color}
      data-test="followed course bar"
    >
      <div className="flex items-center gap-1">
        <IconWorkflowMerge className="size-4 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1.5 [overflow-wrap:anywhere]">
          <span className="font-bold" data-test="followed course name">
            Following: {followedCourse.attributes.title}
          </span>
          <span data-test="followed course no access">You don't have access to this class.</span>
        </div>
      </div>
    </div>
  )
})

// The course's own color, darkened, so the stack reads as one card with the title bar below it
// rather than as its own thing.
const barClassName = cx(
  "set-course-color-vars bg-[color-mix(in_srgb,var(--course-color)_90%,black)]",
  "flex cursor-default flex-col border-b border-black/10 px-2.5 py-1",
  "text-[0.8125rem] leading-none select-none",
)

const FollowedCourseUnits = tracked(function FollowedCourseUnits({
  followedCourse,
  followedPlanbook,
  followedRotationCalendar,
  lessonModels,
  dateString,
  editCourseTitleAndOrder,
}: {
  followedCourse: Course
  followedPlanbook: Planbook
  followedRotationCalendar: RotationCalendar | null
  /** The wrapping course's day, which a unit row opens the comparison modal against. */
  lessonModels: { course: Course; planbook: Planbook; rotationCalendar: RotationCalendar }
  dateString: string
  editCourseTitleAndOrder: (courseId: string) => void
}) {
  const courseDate =
    followedRotationCalendar !== null
      ? calculateCourseDateCombined(
          dateString,
          followedCourse,
          followedPlanbook,
          followedRotationCalendar,
          // We don't need or want to create the phantom lesson.
          () => null,
        )
      : null
  const units = useDocumentsTogether("card-stack-summary", courseDate?.unitIds ?? []).value ?? null

  if (followedRotationCalendar === null) return null

  return (
    <UnitsInHeader
      units={units}
      course={followedCourse}
      planbook={followedPlanbook}
      rotationCalendar={followedRotationCalendar}
      dateString={dateString}
      editCourseTitleAndOrder={editCourseTitleAndOrder}
      lessonModels={lessonModels}
      showUnitReadonly
      flush
      className="mb-1.5"
      pillClassName="bg-gray-300"
    />
  )
})
